import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  FormControl,
  MenuItem,
  Button,
  TextField,
  Paper,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  IconButton,
  Chip,
  Stack
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { SkillResource, WorkAssignmentWithScore } from '../types/microsoftGraph';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { Subject, Course, Skill, SkillsMapElement, SkillNode, SkillLink, emptySkillNode, EmptySkillLink, LessonFile } from '../types/microsoftGraph';
import SkillDetailsDialog from './SkillDetailsDialogue';
import { SkillsIndexTable } from './SkillsIndexTable'; // ✅ Teacher/Admin view
import { SkillsScoreTable } from './SkillsScoreTable'; // ✅ Student view
import { api, API_ENDPOINTS } from '../config/api';

// Types based on PHP structures and your existing types

interface SkillsTreeProps {
  courseId?: number;
  courseName?: string;
  readOnly?: boolean;
  individualId?: number;
}

interface STParents {
  nSkillID: number;
}

interface STOffspring {
  nSkillID: number;
  nLinkID?: number;
}

interface SkillKeyEntry {
  key: number;
  name: string;
  averageMark: number;
  markCount: number;
}


// Styled components matching PHP visual design
const HeaderContainer = styled(Box)(({ theme }) => ({
  height: '116px',
  backgroundColor: '#1D1D29',
  color: 'white',
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(2),
  position: 'relative'
}));

const ContentContainer = styled(Box)(({ theme }) => ({
  backgroundColor: '#3C3A37',
  minHeight: '584px',
  height: 'calc(100vh - 200px)', // ✅ ADD: Take remaining viewport height
  display: 'flex',
  padding: theme.spacing(2),
  gap: theme.spacing(1)
}));

const SkillsCanvas = styled('canvas')(({ theme }) => ({
  border: '1px solid #151616',
  backgroundColor: '#FDFFEF',
  cursor: 'crosshair',
  borderRadius: theme.spacing(0.5),
  display: 'block',
  width: '100% !important',
  height: '100% !important',
  '&::before': { content: 'none' },
  '&::after': { content: 'none' },
}));

const ControlPanel = styled(Card)(({ theme }) => ({
  backgroundColor: '#F7F7F9',
  boxShadow: '1px 1px 5px 0 rgba(0,0,0,0.06)',
  transition: 'all 300ms ease-out',
  '&:hover': {
    boxShadow: '5px 5px 20px 5px rgba(87,205,218,0.10)',
  },
  maxWidth: '400px',
  margin: '0 auto',
  marginTop: theme.spacing(4)
}));

const HelpDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialogContent-root': {
    backgroundColor: '#A55A02',
    color: '#FFFFFF',
    minHeight: '445px',
    width: '1054px'
  },
}));

const LoaderContainer = styled(Box)(({ theme }) => ({
  position: 'absolute',
  right: '76px',
  top: '2px',
  width: '60px',
  height: '46px',
  zIndex: 3
}));

// Debug interfaces
interface DebugState {
  apiBaseUrl: string;
  hasToken: boolean;
  componentMounted: boolean;
  fetchAttempted: boolean;
  fetchSuccessful: boolean;
  currentError: string | null;
  logs: DebugLog[];
}

interface DebugLog {
  timestamp: string;
  level: 'info' | 'warn' | 'error';
  component: string;
  message: string;
  data?: any;
}

interface NodesAndLinks {
  nodes: SkillNode[];
  links: SkillLink[];
}
interface Competencycolouring {
  CompetencyID: number;
  Colour: string;
  aveScore: number;
  SkillID: number;
  Attempts?: number;
}

function EmptyCompetencycolouring(): Competencycolouring {
  return {
    CompetencyID: 0,
    Colour: '',
    aveScore: 0,
    SkillID: 0
  };
}


const SkillsTree: React.FC<SkillsTreeProps> = ({
courseId,
  courseName,
  readOnly,
  individualId
}) => {

  const propCourseId = courseId;
  const propCourseName = courseName;
  const propIndividualID = individualId;

  const navigate = useNavigate();
  const { token, user } = useAuth(); // ✅ Get user from auth context

  // ✅ Determine effective individual ID (prop overrides logged-in user)
  const effectiveIndividualID = individualId || (user?.userId ? Number(user.userId) : 0);

  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [courses, setCourses] = useState<Course[]>([]);
  const [selectedSubject, setSelectedSubject] = useState<number>(0);
  const [selectedCourse, setSelectedCourse] = useState<number>(0);
  const [skillMaps, setSkillMaps] = useState<SkillsMapElement[]>([]);
    const [skillLinks, setSkillLinks] = useState<SkillLink[]>([]);
  const [skillNodes, setSkillNodes] = useState<SkillNode[]>([]);
  const [skillKey, setSkillKey] = useState<SkillKeyEntry[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [helpOpen, setHelpOpen] = useState<boolean>(false);
  const [currentPlan, setCurrentPlan] = useState<number>(propCourseId && propCourseId > 0 ? 1 : 2); // ✅ Default to selection view if no propCourseId
  const [selectedSubjectForCourses, setSelectedSubjectForCourses] = useState<number | null>(null);
  const [selectedCourseForTopics, setSelectedCourseForTopics] = React.useState<number | null>(null);
  const [hoveredSkill, setHoveredSkill] = useState<{ x: number; y: number; name: string } | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [canvasDimensions, setCanvasDimensions] = useState({ width: 862, height: 536 });
const [containerReady, setContainerReady] = useState(false);
const [skillResources, setSkillResources] = useState<LessonFile[]>([]);

  // Competencies state
  const [competenciesState, setCompetenciesState] = useState<any[] | null>(null);
  const [competencyColors, setCompetencyColors] = useState<Competencycolouring[]>([]);

  // Debug state
  const [debugState, setDebugState] = useState<DebugState>({
    apiBaseUrl: '',
    hasToken: false,
    componentMounted: false,
    fetchAttempted: false,
    fetchSuccessful: false,
    currentError: null,
    logs: []
  });
  const [showDebug, setShowDebug] = useState(true);

// Handle tab change in the dialog
const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
  if (!selectedSkillDetails) return;
  
  // Load data for the selected tab
  switch (newValue) {
    case 1: // Work assignments tab
      if (workAssignments.length === 0) {
        fetchAttemptsData(selectedSkillDetails.skillID);
      }
      break;
    case 2: // Resources tab
      // ✅ FIX: Check skillResources (which is passed to dialog) instead of resources
      if (skillResources.length === 0) {
        fetchSkillResources(selectedSkillDetails.skillID);
      }
      break;
  }
};

  // In SkillsTree.tsx, add these state variables and functions:

// State for skill details dialog
const [selectedSkillDetails, setSelectedSkillDetails] = useState<Skill | null>(null);
const [skillDetailsOpen, setSkillDetailsOpen] = useState(false);
const [workAssignments, setWorkAssignments] = useState<WorkAssignmentWithScore[]>([]);
const [resources, setResources] = useState<SkillResource[]>([]);
const [loadingTab, setLoadingTab] = useState<number | null>(null);

// Function to handle skill click
const handleSkillClick = async (skillID: number) => {
  console.log('[handleSkillClick] Fetching details for skill:', skillID);
  
  // Reset all state
  setSkillDetailsOpen(true);
  setSelectedSkillDetails(null);
  setWorkAssignments([]);
  setResources([]);
  setLoadingTab(null);
  
  // Fetch basic skill details immediately
  fetchBasicSkillDetails(skillID);
};

// ✅ UPDATE: Load basic details AND resources immediately
const fetchBasicSkillDetails = async (skillID: number) => {
  try {
    console.log('[fetchBasicSkillDetails 1] Fetching skill:', skillID);
    const data = await api.get(API_ENDPOINTS.skills.get(skillID));
    console.log('[fetchBasicSkillDetails 2] Received:', data);
    
    // Helper to normalize hidden field
    const normalizeHidden = (hidden: any): number => {
        if (typeof hidden === 'number') return hidden;
        if (hidden && typeof hidden === 'object' && 'Int16' in hidden) {
          return hidden.Int16;
        }
        return 0;
      };
      
      const skillData: Skill = {
        skillID: data.skillID || skillID,
        skillName: data.skillName || `Skill ${skillID}`,
        skillDescription: data.skillDescription || '',
        developmentAge: data.developmentAge || '',
        criteria1: data.criteria1 || '',
        criteria2: data.criteria2 || '',
        criteria3: data.criteria3 || '',
        criteria4: data.criteria4 || '',
        criteria5: data.criteria5 || '',
        hidden: normalizeHidden(data.hidden),
        parentSkills: data.parentSkills || [],
        offspringSkills: data.offspringSkills || []
      };
      
      console.log('[fetchBasicSkillDetails 3] Transformed skill data:', skillData);
      setSelectedSkillDetails(skillData);

      // ✅ Fetch resources immediately (don't wait for tab click)
      await fetchSkillResources(skillID);
      
      // ✅ If we have an individualId, fetch work assignments too
      if (effectiveIndividualID && effectiveIndividualID > 0) {
        await fetchAttemptsData(skillID);
      }
    } catch (err) {
    console.error('Error fetching skill details:', err);
  }
};

// ✅ UPDATE: Fetch lesson files for the skill
const fetchSkillResources = async (skillID: number) => {
  try {
    console.log('[fetchSkillResources] Fetching resources for skill:', skillID);
    const data = await api.get(`${API_ENDPOINTS.skills.get(skillID).replace(/\/\d+$/, '')}/resources/${skillID}`);
    console.log('[fetchSkillResources] Received resources:', data);
    
    const resourcesArray = data.resources || [];
    
    const lessonFiles: LessonFile[] = resourcesArray.map((resource: any) => ({
      lessonFilesId: parseInt(resource.id) || 0,
      filename: resource.title || '',
      fileDescription: resource.description || '',
      audioFile: '',
      fileTypesId: 0,
      mimetypesId: 0,
      skillsId: skillID,
      timeReq: parseInt(resource.duration) || 0,
      localFoldersId: 0,
      hidden: 0,
      cloudId: resource.cloudId || '',
      fileType: resource.type || 'File',
      mimeType: ''
    }));
    
    const uniqueFiles = lessonFiles.filter((file, index, self) =>
      index === self.findIndex(f => f.lessonFilesId === file.lessonFilesId)
    );
    
    console.log('[fetchSkillResources] Transformed to LessonFiles:', uniqueFiles);
    setSkillResources(uniqueFiles);
  } catch (err) {
    console.error('Error fetching skill resources:', err);
    setSkillResources([]);
  }
};

// Fetch attempts when Tab 1 is clicked
const fetchAttemptsData = async (skillID: number) => {
  if (!effectiveIndividualID || !token) {
    console.log('[fetchAttemptsData] No individual ID or token available');
    return;
  }
  
  console.log('[fetchAttemptsData] Fetching attempts for skill:', skillID, 'individual:', effectiveIndividualID);
  setLoadingTab(1);
  
  try {
    const data = await api.get(`${API_ENDPOINTS.skills.get(skillID).replace(/\/\d+$/, '')}/attempts/${skillID}/${effectiveIndividualID}`);
    console.log('[fetchAttemptsData] Received:', data);
    
    const transformedAssignments: WorkAssignmentWithScore[] = data.attemptsDetails?.map((attempt: any) => ({
      assignmentID: attempt.workAssignmentID,
      assignmentName: attempt.lessonDescription || 'Assignment',
      fileName: attempt.lessonFileName || '',
      score: attempt.skillMark || 0,
      maxScore: 100, 
      dateCompleted: attempt.doneDate ? Math.floor(new Date(attempt.doneDate).getTime() / 1000) : 0,
      percentageScore: attempt.skillMark || 0,
    })) || [];
    
    setWorkAssignments(transformedAssignments);
  } catch (err) {
    console.error('Error fetching attempts:', err);
  } finally {
    setLoadingTab(null);
  }
};

// Fix the save function to match backend expectations
const saveSkillsMapToDatabase = async (CourseID: number, nodes: SkillNode[], links: SkillLink[]): Promise<void> => {
  try {
    console.log('Saving skills tree to database:', { CourseID, nodes: nodes, links: links });
    
    const payload = {
      CourseID: CourseID,
      Nodes: nodes,
      Links: links
    };

    console.log('Sending payload to backend:', payload);

    await api.post(API_ENDPOINTS.skillsMap.create, payload);

    console.log('skills tree saved successfully');
  } catch (err) {
    console.error('Error saving skills tree:', err);
    throw new Error('Failed to save skills tree to database: ' + (err as Error).message);
  }
};

// ✅ SIMPLIFIED: drawSkillsMap just sorts and numbers in one pass
const drawSkillsMap = useCallback((maps: SkillNode[], links: SkillLink[]) => {
  const canvas = canvasRef.current;
  if (!canvas) return;

  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  ctx.fillStyle = '#FDFFEF';
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  if (!maps || maps.length === 0) {
    console.log('[drawSkillsMap] No nodes to draw');
    return;
  }

  // ✅ Sort nodes to get consistent numbering
  const sortedNodes = [...maps].sort((a, b) => {
    if (a.YPos !== b.YPos) {
      return a.YPos - b.YPos;
    }
    return a.XPos - b.XPos;
  });

  // ✅ Create map: SkillID -> KeyNum
  const keyNumMap = new Map<number, number>();
  sortedNodes.forEach((node, index) => {
    keyNumMap.set(node.SkillID, index + 1);
  });

  // ✅ DEBUG: Log the mapping
  console.log('[drawSkillsMap] KeyNum mapping:', 
    Array.from(keyNumMap.entries())
      .sort((a, b) => a[1] - b[1])
      .slice(0, 10)
      .map(([id, num]) => `SkillID ${id} -> #${num}`)
  );

  const competencyMapStr = sessionStorage.getItem('competencyMap');
  const cachedCompetencyMap = competencyMapStr ? JSON.parse(competencyMapStr) : {};

  const CANVAS_WIDTH = canvas.width;
  const CANVAS_HEIGHT = canvas.height;
  const radius = 20;

  const maxXPos = Math.max(1, ...maps.map(m => m.XPos || 1));
  const maxYPos = Math.max(1, ...maps.map(m => m.YPos || 1));

  const xPadding = 60;
  const yPadding = 60;
  const availableWidth = CANVAS_WIDTH - (xPadding * 2);
  const availableHeight = CANVAS_HEIGHT - (yPadding * 2);

  let nodeSpacing = maxXPos > 1 ? availableWidth / (maxXPos + 1) : 80;
  let verticalSpacing = maxYPos > 1 ? availableHeight / (maxYPos + 1) : 80;

  const MIN_NODE_SPACING = 80;
  nodeSpacing = Math.max(nodeSpacing, MIN_NODE_SPACING);
  verticalSpacing = Math.max(verticalSpacing, MIN_NODE_SPACING);

  const mapWidth = (maxXPos - 1) * nodeSpacing;
  const mapHeight = (maxYPos - 1) * verticalSpacing;

  const xInset = (CANVAS_WIDTH - mapWidth) / 2;
  const yInset = (CANVAS_HEIGHT - mapHeight) / 2;

  const getCoords = (node: SkillNode) => ({
    x: xInset + (node.XPos - 1) * nodeSpacing,
    y: yInset + (node.YPos - 1) * verticalSpacing,
  });

  const skillKeyData: SkillKeyEntry[] = [];
  const nodeMap = new Map<number, SkillNode>(maps.map(n => [n.SkillID, n]));

  // Draw links first
  ctx.strokeStyle = '#CCCCCC';
  ctx.lineWidth = 2;

  for (const link of links) {
    const parentNode = nodeMap.get(link.ParentSkillID);
    const offspringNode = nodeMap.get(link.OffspringSkillID);
    if (!parentNode || !offspringNode) continue;

    const parentCoords = getCoords(parentNode);
    const offspringCoords = getCoords(offspringNode);

    ctx.beginPath();
    ctx.moveTo(parentCoords.x, parentCoords.y);
    ctx.lineTo(offspringCoords.x, offspringCoords.y);
    ctx.stroke();
  }

  // Draw nodes with numbering
  maps.forEach((map) => {
    const nodeNumber = keyNumMap.get(map.SkillID) || 0;
    const { x, y } = getCoords(map);

    let cachedScore = 0;
    let cachedAttempts = 0;
    const cachedData = cachedCompetencyMap[map.SkillID];
    
    if (typeof cachedData === 'number') {
      cachedScore = cachedData;
    } else if (cachedData && typeof cachedData === 'object') {
      cachedScore = cachedData.score || 0;
      cachedAttempts = cachedData.attempts || 0;
    }

    const score = map.AverageSkillScore ?? cachedScore ?? 0;
    const nodeColor = '#FFFFFF';
    
    ctx.fillStyle = nodeColor;
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(x, y, radius, 0, 2 * Math.PI);
    ctx.fill();
    ctx.stroke();

    ctx.fillStyle = score > 0 ? '#FFFFFF' : '#000000';
    ctx.font = 'bold 16px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(nodeNumber.toString(), x, y);

    skillKeyData.push({
      key: nodeNumber,
      name: map.SkillName,
      averageMark: Number(score),
      markCount: cachedAttempts,
    });
  });

  setSkillKey(skillKeyData);
  
  // ✅ Update nodes with KeyNum for table display
  const updatedNodes = maps.map(node => ({
    ...node,
    KeyNum: keyNumMap.get(node.SkillID) || 0
  }));
  
  console.log('[drawSkillsMap] Updated nodes sample:', 
    updatedNodes.slice(0, 5).map(n => `${n.SkillName} (ID: ${n.SkillID}): KeyNum=${n.KeyNum}, Pos=(${n.XPos},${n.YPos})`)
  );
  
  setSkillNodes(updatedNodes);

}, [canvasDimensions.height, canvasDimensions.width]);

const scheduleDrawSkillsMap = (maps: SkillNode[], links: SkillLink[]) => {
    const attemptDraw = () => {
      const canvas = canvasRef.current;
      if (!canvas) {
        requestAnimationFrame(attemptDraw);
        return;
      }
      drawSkillsMap(maps, links);
    };
    attemptDraw();
  };

  const ElementToNode = (element: any): SkillNode => {
  const skillID = Number(element.Skills_keyID ?? element.Skills_keyID ?? element.SkillID ?? element.skillID ?? 0);
  const skillName = element.Skill_name ?? element.skillName ?? `Skill ${skillID}`;
  const xRaw = Number(element.PointAx ?? element.PointBx ?? element.pointAx ?? element.pointBx ?? 1);
  const yRaw = Number(element.PointAy ?? element.PointBy ?? element.pointAy ?? element.pointBy ?? 1);

  const xPos = Math.max(1, Math.round(isNaN(xRaw) ? 1 : xRaw));
  const yPos = Math.max(1, Math.round(isNaN(yRaw) ? 1 : yRaw));

  return {
    SkillID: skillID,
    SkillName: skillName,
    XPos: xPos,
    YPos: yPos,
    KeyNum: 0,
    Parents: [],
    Offspring: [],
    AverageSkillScore: 0,
  };
};

function convertElementsToNodesAndLinks(skillMaps: any[]): NodesAndLinks {
  console.log('[convertElementsToNodesAndLinks] Input:', skillMaps);
  
  var nodes: SkillNode[] = [];
  var links: SkillLink[] = [];
  
  if (!skillMaps || skillMaps.length === 0) {
    console.log('[convertElementsToNodesAndLinks] No input data');
    return { nodes, links };
  }
  
  const sampleElement = skillMaps[0];
  console.log('[convertElementsToNodesAndLinks] Sample element:', sampleElement);
  
  const allZeroSkillIDs = skillMaps.every(element => 
    (!element.Skills_keyID || element.Skills_keyID === 0) && 
    (!element.Skill_linkID || element.Skill_linkID === 0)
  );
  
  if (allZeroSkillIDs) {
    console.log('[convertElementsToNodesAndLinks] All elements have zero IDs - invalid map data');
    return { nodes, links };
  }
  
  // ✅ BUILD POSITION-TO-SKILLID MAP FIRST
 const positionToSkillId = new Map<string, number>();

  skillMaps.forEach(element => {
    if (element.Skills_keyID && element.Skills_keyID > 0) {
      const node: SkillNode = ElementToNode(element);
      nodes.push(node);

      const posKey = `${element.PointAx},${element.PointAy}`;
      if (element.PointAx !== undefined && element.PointAy !== undefined) {
        positionToSkillId.set(posKey, element.Skills_keyID);
      }
    }
  });

  skillMaps.forEach(element => {
    if (element.Skill_linkID && element.Skill_linkID > 0) {
      // Try coordinate lookup first, then fallback to explicit IDs
      const parentPosKey = `${element.PointAx},${element.PointAy}`;
      const parentSkillId = positionToSkillId.get(parentPosKey) ??
                            (element.ParentSkillID && element.ParentSkillID > 0 ? element.ParentSkillID : undefined);

      const offspringPosKey = `${element.PointBx},${element.PointBy}`;
      const offspringSkillId = (element.OffspringSkillID && element.OffspringSkillID > 0) ?
                                element.OffspringSkillID : positionToSkillId.get(offspringPosKey);

      if (parentSkillId && offspringSkillId) {
        const link: SkillLink = {
          Skill_linkID: element.Skill_linkID,
          ParentSkillID: parentSkillId,
          OffspringSkillID: offspringSkillId,
          PosAx: element.PointAx,
          PosAy: element.PointAy,
        };
        links.push(link);
      } else {
        console.warn(`[convertElementsToNodesAndLinks] Missing IDs for link ${element.Skill_linkID}`, {
          parentPos: parentPosKey,
          parentSkillId,
          offspringPos: offspringPosKey,
          offspringSkillId
        });
      }
    }
  });

  console.log('[convertElementsToNodesAndLinks] Results:', { 
    totalInput: skillMaps.length,
    nodes: nodes.length,
    links: links.length
  });

  return { nodes, links };
}


const convertSkillNodesToNodesAndLinks = (rawData: any[]): {nodes: SkillNode[], links: SkillLink[]} => {
  console.log('[convertSkillNodesToNodesAndLinks] Input count:', rawData.length);
  
  const nodes: SkillNode[] = [];
  const links: SkillLink[] = [];
  
  // Track skill IDs to avoid duplicates and for validation
  const skillIdSet = new Set<number>();
  const nodeMap = new Map<number, SkillNode>();
  
  // First pass: Collect all skills and create nodes
  rawData.forEach(item => {
    if (item.Skills_keyID && item.Skills_keyID > 0) {
      // Avoid duplicates
      if (!skillIdSet.has(item.Skills_keyID)) {
        skillIdSet.add(item.Skills_keyID);
        
        const skillNode: SkillNode = {
          SkillID: item.Skills_keyID,
          SkillName: item.Skill_name || '',
          skillName: item.Skill_name || '',
          XPos: item.PointAx || 0,
          YPos: item.PointAy || 0,
          yPos: item.PointAy || 0,
          KeyNum: 0,
          Parents: [],
          parents: [],
          Offspring: [],
          offspring: [],
          AverageSkillScore: 0,
          averageSkillScore: 0,
          SkillAttempts: 0
        };
        nodes.push(skillNode);
        nodeMap.set(skillNode.SkillID, skillNode);
      }
    }
  });
  
  console.log(`[convertSkillNodesToNodesAndLinks] Found ${nodes.length} skills:`, 
    nodes.map(n => n.SkillID));
  
  // Second pass: Collect all valid links AND populate node relationships
  rawData.forEach(item => {
    if (item.Skill_linkID && item.Skill_linkID > 0 && 
        item.ParentSkillID && item.ParentSkillID > 0 &&
        item.OffspringSkillID && item.OffspringSkillID > 0) {
      
      // Validate that both skills exist in our node list
      const parentExists = skillIdSet.has(item.ParentSkillID);
      const offspringExists = skillIdSet.has(item.OffspringSkillID);
      
      if (parentExists && offspringExists) {
        const skillLink: SkillLink = {
          Skill_linkID: item.Skill_linkID,
          ParentSkillID: item.ParentSkillID,
          OffspringSkillID: item.OffspringSkillID,
          PosAx: item.PointAx || 0,
          PosAy: item.PointAy || 0
        };
        links.push(skillLink);

        // ✅ NOW POPULATE NODE RELATIONSHIPS
        const parentNode = nodeMap.get(item.ParentSkillID);
        const offspringNode = nodeMap.get(item.OffspringSkillID);
        
        if (parentNode && offspringNode) {
          // Add offspring to parent
          if (!parentNode.Offspring.includes(item.OffspringSkillID)) {
            parentNode.Offspring.push(item.OffspringSkillID);
            // parentNode.offspring.push(item.OffspringSkillID); // Keep lowercase alias in sync
          }
          
          // Add parent to offspring
          if (!offspringNode.Parents.includes(item.ParentSkillID)) {
            offspringNode.Parents.push(item.ParentSkillID);
            // offspringNode.parents.push(item.ParentSkillID); // Keep lowercase alias in sync
          }
          
          console.log(`[convertSkillNodesToNodesAndLinks] Linked: ${item.ParentSkillID} -> ${item.OffspringSkillID}`);
        }
      } else {
        console.warn(`Skipping link ${item.Skill_linkID}: Parent ${item.ParentSkillID} (exists: ${parentExists}) or offspring ${item.OffspringSkillID} (exists: ${offspringExists}) not in course`);
      }
    }
  });
  
  // Assign KeyNum (sequential numbering)
  nodes.forEach((node, index) => {
    node.KeyNum = index + 1;
  });
  
  console.log('[convertSkillNodesToNodesAndLinks] Results:', {
    nodes: nodes.length,
    links: links.length,
    sampleSkills: nodes.slice(0, 3).map(n => ({ 
      id: n.SkillID, 
      name: n.SkillName,
      parents: n.Parents,
      offspring: n.Offspring
    })),
    sampleLinks: links.slice(0, 3)
  });
  
  return { nodes, links };
};



  const handleCanvasMouseMove = (event: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas || skillNodes.length === 0) {
      setHoveredSkill(null);
      return;
    }

    const rect = canvas.getBoundingClientRect();
    const mouseX = event.clientX - rect.left;
    const mouseY = event.clientY - rect.top;

    const radius = 20;

    const maxXPos = Math.max(...skillNodes.map(m => m.XPos));
    const maxYPos: number = Math.max(1, ...skillNodes.map((m: SkillNode) => m.YPos || 1));

    const xPadding = 60;
    const yPadding = 60;
    const availableWidth = canvasDimensions.width - (xPadding * 2);
    const availableHeight = canvasDimensions.height - (yPadding * 2);

    let nodeSpacing = maxXPos > 1
      ? availableWidth / (maxXPos + 1)
      : 80;
    let verticalSpacing = maxYPos > 1
      ? availableHeight / (maxYPos + 1)
      : 80;

    const MIN_NODE_SPACING = 80;
    if (nodeSpacing < MIN_NODE_SPACING) {
      nodeSpacing = MIN_NODE_SPACING;
    }
    if (verticalSpacing < MIN_NODE_SPACING) {
      verticalSpacing = MIN_NODE_SPACING;
    }

    const mapWidth = (maxXPos - 1) * nodeSpacing;
    const mapHeight = (maxYPos - 1) * verticalSpacing;

    const xInset = (canvasDimensions.width - mapWidth) / 2;
    const yInset = (canvasDimensions.height - mapHeight) / 2;

    // Use IDENTICAL coordinate calculation as drawSkillsMap
    const getCoords = (node: SkillNode) => {
      const x = xInset + (node.XPos - 1) * nodeSpacing;
      const y = yInset + (node.YPos - 1) * verticalSpacing;
      return { x, y };
    };

    // Check if mouse is over any skill node
    for (const node of skillNodes) {
      const coords = getCoords(node);
      const distance = Math.sqrt(
        Math.pow(mouseX - coords.x, 2) + Math.pow(mouseY - coords.y, 2)
      );

      if (distance <= radius) {
        setHoveredSkill({ x: mouseX, y: mouseY, name: node.SkillName });
        canvas.style.cursor = 'pointer';
        return;
      }
    }

    setHoveredSkill(null);
    canvas.style.cursor = 'crosshair';
  };

  const handleCanvasMouseLeave = () => {
    setHoveredSkill(null);
    const canvas = canvasRef.current;
    if (canvas) {
      canvas.style.cursor = 'crosshair';
    }
  };

  const coursesBySubject = subjects.reduce((acc, subject) => {
    acc[subject.SubjectID] = courses.filter(c => c.SubjectID === subject.SubjectID);
    return acc;
  }, {} as Record<number, Course[]>);

  // ✅ MOVE: Define ordering helper functions BEFORE shuffleNodes
  const getAveragePosition = (nodeIds: number[], level: SkillNode[]): number => {
    const positions = nodeIds
      .map(id => level.find(n => n.SkillID === id)?.XPos)
      .filter(pos => pos !== undefined) as number[];
    
    if (positions.length === 0) return 0;
    return positions.reduce((sum, pos) => sum + pos, 0) / positions.length;
  };

  const getAverageParentPosition = (node: SkillNode, parentLevel: SkillNode[]): number => {
    if (!Array.isArray(node.Parents) || node.Parents.length === 0) {
      return 0;
    }
    
    const parentPositions = node.Parents
      .map(parentId => parentLevel.find(p => p.SkillID === parentId)?.XPos)
      .filter(pos => pos !== undefined) as number[];
    
    if (parentPositions.length === 0) return 0;
    return parentPositions.reduce((sum, pos) => sum + pos, 0) / parentPositions.length;
  };

  const getAverageChildPosition = (node: SkillNode, childLevel: SkillNode[]): number => {
    if (!Array.isArray(node.Offspring) || node.Offspring.length === 0) {
      return 0;
    }
    
    const childPositions = node.Offspring
      .map(childId => childLevel.find(c => c.SkillID === childId)?.XPos)
      .filter(pos => pos !== undefined) as number[];
    
    if (childPositions.length === 0) return 0;
    return childPositions.reduce((sum, pos) => sum + pos, 0) / childPositions.length;
  };

  // Order root level by grouping nodes that share the same children
  const orderRootLevel = (currentLevel: SkillNode[], nextLevel: SkillNode[]): SkillNode[] => {
    if (!nextLevel) return currentLevel;
    
    // Create groups based on which child nodes they connect to
    const groups: Map<string, SkillNode[]> = new Map();
    
    currentLevel.forEach(node => {
      if (Array.isArray(node.Offspring) && node.Offspring.length > 0) {
        // Create a group key based on sorted offspring IDs
        const groupKey = node.Offspring.sort((a, b) => a - b).join(',');
        
        if (!groups.has(groupKey)) {
          groups.set(groupKey, []);
        }
        groups.get(groupKey)!.push(node);
      } else {
        // Nodes with no offspring go in their own group
        const groupKey = `no-offspring-${node.SkillID}`;
        groups.set(groupKey, [node]);
      }
    });
    
    // Sort groups by the position of their children in the next level
    const sortedGroups = Array.from(groups.entries()).sort(([keyA], [keyB]) => {
      if (keyA.startsWith('no-offspring')) return 1;
      if (keyB.startsWith('no-offspring')) return -1;
      
      const childIdsA = keyA.split(',').map(Number);
      const childIdsB = keyB.split(',').map(Number);
      
      const avgPosA = getAveragePosition(childIdsA, nextLevel);
      const avgPosB = getAveragePosition(childIdsB, nextLevel);
      
      return avgPosA - avgPosB;
    });
    
    // Flatten the sorted groups into final order
    const result: SkillNode[] = [];
    sortedGroups.forEach(([_, groupNodes]) => {
      // Sort within group by SkillID for consistency
      groupNodes.sort((a, b) => a.SkillID - b.SkillID);
      result.push(...groupNodes);
    });
    
    console.log('[orderRootLevel] Root level groups:', sortedGroups.map(([key, nodes]) => 
      `${key}: [${nodes.map(n => n.SkillID).join(',')}]`
    ));
    
    return result;
  };

  // Order middle levels based on both parent and child connections
  const orderMiddleLevel = (currentLevel: SkillNode[], prevLevel: SkillNode[], nextLevel: SkillNode[]): SkillNode[] => {
    return currentLevel.sort((a, b) => {
      // Calculate average position of parents
      const aParentPos = getAverageParentPosition(a, prevLevel);
      const bParentPos = getAverageParentPosition(b, prevLevel);
      
      // Calculate average position of children  
      const aChildPos = getAverageChildPosition(a, nextLevel);
      const bChildPos = getAverageChildPosition(b, nextLevel);
      
      // Combine both (weight parents slightly more for stability)
      const aScore = aParentPos * 0.6 + aChildPos * 0.4;
      const bScore = bParentPos * 0.6 + bChildPos * 0.4;
      
      return aScore - bScore;
    });
  };

  // Order last level based on only parent connections
  const orderLastLevel = (currentLevel: SkillNode[], prevLevel: SkillNode[]): SkillNode[] => {
    return currentLevel.sort((a, b) => {
      const aPos = getAverageParentPosition(a, prevLevel);
      const bPos = getAverageParentPosition(b, prevLevel);
      return aPos - bPos;
    });
  };

  const shuffleNodes = async (nodes: SkillNode[], links: SkillLink[]): Promise<SkillNode[]> => {
    console.log('[shuffleNodes] Starting intelligent ordering layout with', nodes.length, 'nodes and', links.length, 'links');
    
    if (nodes.length === 0) {
      console.warn('[shuffleNodes] No nodes provided');
      return nodes;
    }

    const nodeMap = new Map<number, SkillNode>();
    nodes.forEach(node => nodeMap.set(node.SkillID, node));

    // Build parent/child relationships from links (in case node.Parents/Offspring aren't populated)
    const parentMap = new Map<number, Set<number>>(); // childID -> Set of parentIDs
    const childMap = new Map<number, Set<number>>();  // parentID -> Set of childIDs
    
    nodes.forEach(node => {
      parentMap.set(node.SkillID, new Set());
      childMap.set(node.SkillID, new Set());
    });

    links.forEach(link => {
      if (link.ParentSkillID && link.OffspringSkillID) {
        // Add to parentMap: child -> parents
        if (parentMap.has(link.OffspringSkillID)) {
          parentMap.get(link.OffspringSkillID)!.add(link.ParentSkillID);
        }
        // Add to childMap: parent -> children
        if (childMap.has(link.ParentSkillID)) {
          childMap.get(link.ParentSkillID)!.add(link.OffspringSkillID);
        }
      }
    });

    console.log('[shuffleNodes] Built relationship maps from links');

    // Find true roots: nodes with no parents (either from links or from node.Parents)
    const roots = nodes.filter(node => {
      const parentsFromLinks = parentMap.get(node.SkillID) || new Set();
      const parentsFromNode = Array.isArray(node.Parents) ? node.Parents : [];
      return parentsFromLinks.size === 0 && parentsFromNode.length === 0;
    });

    console.log('[shuffleNodes] Found', roots.length, 'root nodes:', roots.map(n => n.SkillID));

    // If no roots found, use nodes with fewest parents as roots
    if (roots.length === 0) {
      console.warn('[shuffleNodes] No root nodes found, using nodes with minimum parents');
      let minParents = Infinity;
      nodes.forEach(node => {
        const parentCount = (parentMap.get(node.SkillID)?.size || 0) + (node.Parents?.length || 0);
        if (parentCount < minParents) minParents = parentCount;
      });
      nodes.forEach(node => {
        const parentCount = (parentMap.get(node.SkillID)?.size || 0) + (node.Parents?.length || 0);
        if (parentCount === minParents) roots.push(node);
      });
    }

    // Level assignment using BFS, but also traverse via links
    const levelMap = new Map<number, number>(); // SkillID -> level index
    const visited = new Set<number>();
    
    // Initialize roots at level 0
    const queue: { node: SkillNode; level: number }[] = [];
    roots.forEach(node => {
      levelMap.set(node.SkillID, 0);
      visited.add(node.SkillID);
      queue.push({ node, level: 0 });
    });

    // BFS through children (using both node.Offspring and childMap from links)
    while (queue.length > 0) {
      const { node, level } = queue.shift()!;
      
      // Get children from both sources
      const childrenFromNode = Array.isArray(node.Offspring) ? node.Offspring : [];
      const childrenFromLinks = childMap.get(node.SkillID) || new Set();
      // ✅ FIX: Convert Set to Array using Array.from()
      const allChildren = new Set([...childrenFromNode, ...Array.from(childrenFromLinks)]);
      
      allChildren.forEach(childId => {
        if (!visited.has(childId) && nodeMap.has(childId)) {
          visited.add(childId);
          levelMap.set(childId, level + 1);
          const childNode = nodeMap.get(childId)!;
          queue.push({ node: childNode, level: level + 1 });
        }
      });
    }

    // Handle any unvisited nodes (disconnected from the main graph)
    nodes.forEach(node => {
      if (!visited.has(node.SkillID)) {
        console.warn('[shuffleNodes] Node', node.SkillID, node.SkillName, 'was not reached via BFS, adding to level 0');
        levelMap.set(node.SkillID, 0);
        visited.add(node.SkillID);
      }
    });

    console.log('[shuffleNodes] Initial level assignments:', Array.from(levelMap.entries()));
    console.log('[shuffleNodes] Visited', visited.size, 'of', nodes.length, 'nodes');

    // Enforce: Parent must be on a strictly higher (smaller index) level than child
    let changed = true;
    let iteration = 0;
    const maxIterations = nodes.length * 3 + 20;

    while (changed && iteration < maxIterations) {
      changed = false;
      iteration++;

      links.forEach(link => {
        const parentId = link.ParentSkillID;
        const childId = link.OffspringSkillID;
        if (!parentId || !childId) return;
        if (!levelMap.has(parentId) || !levelMap.has(childId)) return;

        const parentLevel = levelMap.get(parentId)!;
        const childLevel = levelMap.get(childId)!;

        // Child must be at least one level below parent
        if (childLevel <= parentLevel) {
          const newChildLevel = parentLevel + 1;
          levelMap.set(childId, newChildLevel);
          changed = true;
        }
      });
    }

    if (iteration >= maxIterations) {
      console.warn('[shuffleNodes] Max iterations reached - possible cycle in skill links');
    }

    // Rebuild levels array from corrected levelMap
    const maxLevel = Math.max(...Array.from(levelMap.values()), 0);
    const levels: SkillNode[][] = [];
    for (let i = 0; i <= maxLevel; i++) {
      levels.push([]);
    }
    
    nodes.forEach(node => {
      const lvl = levelMap.get(node.SkillID) ?? 0;
      levels[lvl].push(node);
    });

    // Remove any empty levels (compact)
    const compactedLevels = levels.filter(level => level.length > 0);

    console.log('[shuffleNodes] Corrected levels:', compactedLevels.map(level => level.map(n => n.SkillID)));
    console.log('[shuffleNodes] Total nodes in levels:', compactedLevels.reduce((sum, lvl) => sum + lvl.length, 0));

    // Intelligently order nodes within each level
    for (let i = 0; i < compactedLevels.length; i++) {
      if (i === 0) {
        compactedLevels[i] = orderRootLevel(compactedLevels[i], compactedLevels[i + 1]);
      } else if (i < compactedLevels.length - 1) {
        compactedLevels[i] = orderMiddleLevel(compactedLevels[i], compactedLevels[i - 1], compactedLevels[i + 1]);
      } else {
        compactedLevels[i] = orderLastLevel(compactedLevels[i], compactedLevels[i - 1]);
      }
    }

    console.log('[shuffleNodes] Reordered levels:', compactedLevels.map(level => level.map(n => n.SkillID)));

    // Center each level and assign coordinates
    const maxWidth = Math.max(...compactedLevels.map(level => level.length), 1);
    
    compactedLevels.forEach((level, levelIndex) => {
      const levelWidth = level.length;
      const centerOffset = Math.floor((maxWidth - levelWidth) / 2) + 1;
      
      level.forEach((node, nodeIndex) => {
        node.YPos = levelIndex + 1;
        node.XPos = centerOffset + nodeIndex;
      });
      
      console.log(`[shuffleNodes] Level ${levelIndex + 1}: ${level.length} skills at positions (${level.map(n => `${n.XPos},${n.YPos}`).join(' ')})`);
    });

    console.log('[shuffleNodes] Final node count:', nodes.length);
    return nodes;
  };

  // Canvas dimensions (matching PHP constants)
  const CANVAS_WIDTH = 862;
  const CANVAS_HEIGHT = 536;

  // Load subjects on component mount (converted from A10_COMBO_Subject12)
  useEffect(() => {
    // FIX: Only fetch all subjects/courses if not controlled by a parent prop
    if (!propCourseId) {
      fetchData();
    }
  }, [propCourseId]); // <-- This hook is now ONLY for data fetching.

  // FIX: This hook is now ONLY for managing canvas dimensions.
  useEffect(() => {
    // Run once on mount and then on window resize.
    updateCanvasDimensions();
    window.addEventListener('resize', updateCanvasDimensions);
    return () => window.removeEventListener('resize', updateCanvasDimensions);
  }, []); // <-- Dependency array is now empty to run only on mount/unmount.

  // ✅ Helper function to create headers with auth token
  const getAuthHeaders = useCallback((): HeadersInit => {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    return headers;
  }, [token]);

const generateNewSkillsMap = async (CourseID: number): Promise<{nodes: SkillNode[], links: SkillLink[]}> => {
  try {
    const rawData = await api.get(API_ENDPOINTS.skillAttached.get(CourseID));
    console.log('[generateNewSkillsMap] Raw skill data fetched:', rawData);
    
    if (!rawData) {
      throw new Error('No skills found for this course');
    }
    
    const skillArray = Array.isArray(rawData) ? rawData : [];
    
    if (skillArray.length === 0) {
      throw new Error('No skills found for this course. Please ensure skills are assigned to the course first.');
    }

    const { nodes, links } = convertSkillNodesToNodesAndLinks(skillArray);

    if (nodes.length === 0) {
      throw new Error('No skills found for this course');
    }

    const positionedNodes = await shuffleNodes(nodes, links);
    setSkillLinks(links);
    await saveSkillsMapToDatabase(CourseID, positionedNodes, links);
    scheduleDrawSkillsMap(positionedNodes, links);
    
    return { nodes: positionedNodes, links };
    
  } catch (err) {
    console.error('Error generating skills tree:', err);
    throw new Error('Failed to generate skills tree: ' + (err as Error).message);
  }
};

const loadExistingSkillsMap = async (CourseID: number): Promise<{nodes: SkillNode[], links: SkillLink[]}> => {
  try {
    const data = await api.get(API_ENDPOINTS.skillsMap.get(CourseID));
    console.log('Existing skills tree data fetched:', data);
    
    if (!data || !data.skillMaps || data.skillMaps.length === 0) {
      console.log('No existing skills tree found, generating new one');
      return await generateNewSkillsMap(CourseID);
    }

    var skilAndLink: NodesAndLinks = convertElementsToNodesAndLinks(data.skillMaps);
    setSkillMaps(data.skillMaps);
    setSkillLinks(skilAndLink.links);
    
    scheduleDrawSkillsMap(skilAndLink.nodes, skilAndLink.links);
    return { nodes: skilAndLink.nodes, links: skilAndLink.links };
  } catch (err) {
    console.error('Error loading existing skills tree:', err);
    return await generateNewSkillsMap(CourseID);
  }
};

const colourCompetenciesOnMap = (compDataArray: Competencycolouring[], nodesToColor?: SkillNode[]): void => {
  if (!compDataArray || !Array.isArray(compDataArray) || compDataArray.length === 0) {
    console.log('[colourCompetenciesOnMap] No competency data provided');
    return;
  }

  const canvas = canvasRef.current;
  if (!canvas) {
    console.log('[colourCompetenciesOnMap] No canvas reference');
    return;
  }

  const ctx = canvas.getContext('2d');
  if (!ctx) {
    console.log('[colourCompetenciesOnMap] No canvas context');
    return;
  }

  // Create a map of skill IDs to competency scores
  const competencyMap = new Map<number, number>();
  compDataArray.forEach((comp: Competencycolouring) => {
    if (comp.SkillID && comp.aveScore !== undefined) {
      competencyMap.set(Number(comp.SkillID), Number(comp.aveScore));
      console.log(`[colourCompetenciesOnMap] Mapped SkillID ${comp.SkillID} -> Score ${comp.aveScore}`);
    }
  });

  console.log('[colourCompetenciesOnMap] Competency map:', Array.from(competencyMap.entries()));

  // Use provided nodes or fall back to skillNodes state
  const nodesToDraw = nodesToColor || skillNodes;
  
  if (nodesToDraw.length === 0) {
    console.log('[colourCompetenciesOnMap] No nodes to color');
    return;
  }

  console.log('[colourCompetenciesOnMap] Drawing', nodesToDraw.length, 'nodes');

  // Clear canvas
  ctx.fillStyle = '#FDFFEF';
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  // Recalculate coordinates (same as drawSkillsMap)
  const maxXPos = Math.max(1, ...nodesToDraw.map(m => m.XPos || 1));
  const maxYPos = Math.max(1, ...nodesToDraw.map(m => m.YPos || 1));

  const xPadding = 60;
  const yPadding = 60;
  const availableWidth = canvas.width - (xPadding * 2);
  const availableHeight = canvas.height - (yPadding * 2);

  let nodeSpacing = maxXPos > 1 ? availableWidth / (maxXPos + 1) : 80;
  let verticalSpacing = maxYPos > 1 ? availableHeight / (maxYPos + 1) : 80;

  const MIN_NODE_SPACING = 80;
  nodeSpacing = Math.max(nodeSpacing, MIN_NODE_SPACING);
  verticalSpacing = Math.max(verticalSpacing, MIN_NODE_SPACING);

  const mapWidth = (maxXPos - 1) * nodeSpacing;
  const mapHeight = (maxYPos - 1) * verticalSpacing;

  const xInset = (canvas.width - mapWidth) / 2;
  const yInset = (canvas.height - mapHeight) / 2;

  const getCoords = (node: SkillNode) => ({
    x: xInset + (node.XPos - 1) * nodeSpacing,
    y: yInset + (node.YPos - 1) * verticalSpacing,
  });

  const radius = 20;

  // Redraw nodes with competency colors
  nodesToDraw.forEach((node, index) => {
    const { x, y } = getCoords(node);
    const score = competencyMap.get(node.SkillID) ?? 0;
    const color =  '#FDFFEF';
    console.log(`[colourCompetenciesOnMap] Node ${index + 1} (SkillID: ${node.SkillID}): Score=${score}, Color=${color}`);

    ctx.fillStyle =  '#FDFFEF';
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(x, y, radius, 0, 2 * Math.PI);
    ctx.fill();
    ctx.stroke();

    ctx.fillStyle = '#FFFFFF';
    ctx.font = 'bold 16px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText((index + 1).toString(), x, y);
  });
};

const updateCanvasDimensions = useCallback(() => {
  const canvas = canvasRef.current;
  if (canvas) {
    const parent = canvas.parentElement;
    let width = 862;
    let height = 536;

    if (parent) {
      width = Math.max(parent.clientWidth - 20, 400);
      height = Math.max(parent.clientHeight - 60, 300);
    }

    console.log('[SkillsTree] Setting canvas dimensions:', { width, height });
    setCanvasDimensions({ width, height });

    // Force a redraw after dimensions are set
    if (skillNodes.length > 0) {
      setTimeout(() => {
        console.log('[SkillsTree] Redrawing after dimension change');
        drawSkillsMap(skillNodes, []);
      }, 100);
    }
  }
}, [skillNodes.length, drawSkillsMap]);

// Find the fetchData function (around line 1689) and update it:

const fetchData = async () => {
  setLoading(true);
  setDebugState(prev => ({
    ...prev,
    fetchAttempted: true
  }));

  try {
    const [subjectsData, coursesData] = await Promise.all([
      api.get(API_ENDPOINTS.subjects.list),
      api.get(API_ENDPOINTS.courses.list),
    ]);

    setSubjects(subjectsData || []);
    setCourses(coursesData || []);
    setError(null);

    setDebugState(prev => ({
      ...prev,
      fetchSuccessful: true,
      currentError: null
    }));

  } catch (err) {
    const errorMsg = (err as Error).message;
    setError(`Failed to load subjects and courses: ${errorMsg}`);

    setDebugState(prev => ({
      ...prev,
      fetchSuccessful: false,
      currentError: errorMsg
    }));

    console.error(err);
  } finally {
    setLoading(false);
  }
};


const loadSkillsMap = async (CourseID: number) => {
  if (CourseID <= 0) {
    console.log('[SkillsTree] Invalid CourseID, skipping load:', CourseID);
    return;
  }

  setLoading(true);
  setError('');

  try {
    // ✅ Capture the returned nodes and links
    const mapData = await loadExistingSkillsMap(CourseID);
    setCurrentPlan(1);

   

  } catch (err) {
    console.error('Error loading skills tree:', err);
    // Note: loadExistingSkillsMap already handles generation fallback, 
    // but if something else fails, we catch it here.
    setError('Failed to load skills tree: ' + (err as Error).message);
  } finally {
    setLoading(false);
  }
};

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState<boolean>(false);

  const deleteSkillsMap = async (CourseID: number) => {
    try {
      const result = await api.delete(API_ENDPOINTS.skillsMap.delete(CourseID));

      setSkillNodes([]);
      setSkillKey([]);
      setSkillMaps([]);

      const canvas = canvasRef.current;
      if (canvas) {
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.fillStyle = '#FDFFEF';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
        }
      }

      setDeleteConfirmOpen(false);
      alert(`skills tree deleted successfully (${result.rowsDeleted} rows removed)`);
    } catch (err) {
      console.error('Error deleting skills tree:', err);
      alert(`Failed to delete skills tree: ${(err as Error).message}`);
    }
  };

  const handleDeleteClick = () => {
    if (selectedCourse > 0) {
      setDeleteConfirmOpen(true);
    } else {
      alert('Please select a course first');
    }
  };

  const handleConfirmDelete = () => {
    deleteSkillsMap(selectedCourse);
  };

  // Get skill color based on attainment score (converted from PHP function)


  // Event handlers
  const handleSubjectChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const subjectId = event.target.value as number;
    const subject = subjects.find(s => s.SubjectID === subjectId);
    if (subject) {
      setSelectedSubject(subject.SubjectID);
      setSelectedSubjectForCourses(subject.SubjectID);
    } else {
      setSelectedSubject(0);
      setSelectedSubjectForCourses(null);
    }
    // Reset course and topic when subject changes
    setSelectedCourse(0);
    setSelectedCourseForTopics(null);
  };

  const handleCourseChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const courseId = event.target.value as number;
    const course = courses.find(c => c.CourseID === courseId);
    if (course) {
      setSelectedCourse(course.CourseID);
      setSelectedCourseForTopics(course.CourseID);
    } else {
      setSelectedCourse(0);
      setSelectedCourseForTopics(null);
    }
  };

  // ✅ SINGLE HANDLER - Use this one only
const handleCanvasClick = (event: React.MouseEvent<HTMLCanvasElement>) => {
  const canvas = canvasRef.current;
  if (!canvas || skillNodes.length === 0) return;

  const rect = canvas.getBoundingClientRect();
  const mouseX = event.clientX - rect.left;
  const mouseY = event.clientY - rect.top;

  const radius = 20;

  const maxXPos = Math.max(...skillNodes.map(m => m.XPos));
  const maxYPos = Math.max(...skillNodes.map(m => m.YPos));

  const xPadding = 60;
  const yPadding = 60;
  const availableWidth = canvasDimensions.width - (xPadding * 2);
  const availableHeight = canvasDimensions.height - (yPadding * 2);

  let nodeSpacing = maxXPos > 1 ? availableWidth / (maxXPos + 1) : 80;
  let verticalSpacing = maxYPos > 1 ? availableHeight / (maxYPos + 1) : 80;

  const MIN_NODE_SPACING = 80;
  nodeSpacing = Math.max(nodeSpacing, MIN_NODE_SPACING);
  verticalSpacing = Math.max(verticalSpacing, MIN_NODE_SPACING);

  const mapWidth = (maxXPos - 1) * nodeSpacing;
  const mapHeight = (maxYPos - 1) * verticalSpacing;

  const xInset = (canvasDimensions.width - mapWidth) / 2;
  const yInset = (canvasDimensions.height - mapHeight) / 2;

  const getCoords = (node: SkillNode) => ({
    x: xInset + (node.XPos - 1) * nodeSpacing,
    y: yInset + (node.YPos - 1) * verticalSpacing,
  });

  // Check if any node was clicked
  for (const node of skillNodes) {
    const coords = getCoords(node);
    const distance = Math.sqrt(
      Math.pow(mouseX - coords.x, 2) + Math.pow(mouseY - coords.y, 2)
    );

    if (distance <= radius) {
      console.log('[handleCanvasClick] Clicked on skill:', node.SkillID, node.SkillName);
      handleSkillClick(node.SkillID);  // ✅ Use the dialog system with tabs
      return;
    }
  }
};

  const handleGenerateSkillsMap = () => {
    if (selectedCourse === 0) {
      setError('Please select a course');
      return;
    }
    loadSkillsMap(selectedCourse);
  };

  const handleShowHelp = () => {
    setHelpOpen(true);
  };

  const handleCloseHelp = () => {
    setHelpOpen(false);
  };

  const handleBackToSelection = () => {
    setCurrentPlan(2);
    setSkillMaps([]);
    setSkillKey([]);
  };

  const displayCourseName = propCourseName || courses.find(c => c.CourseID === selectedCourse)?.Course_name || '';

  // ✅ WRAP ENTIRE RETURN IN TRY-CATCH
  const statusColor = (status: any): import("@mui/types").OverridableStringUnion<"default" | "primary" | "secondary" | "error" | "info" | "success" | "warning", import("@mui/material").ChipPropsColorOverrides> | undefined => {
    const statusMap: Record<string, any> = {
      'active': 'success',
      'completed': 'success',
      'in_progress': 'info',
      'pending': 'warning',
      'inactive': 'default',
      'failed': 'error',
      'error': 'error',
    };

    if (!status) return 'default';

    const normalizedStatus = String(status).toLowerCase().trim();
    return statusMap[normalizedStatus] || 'default';
  };

  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', bgcolor: '#2c2f33', p: 2 }}>
      {/* Header */}
      <HeaderContainer>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
          <IconButton onClick={() => navigate(-1)} sx={{ mr: 2 }}>
                                          <ArrowBackIcon />
                                      </IconButton>
          {/* Logo/Icon */}
          <img
                  src={`${process.env.PUBLIC_URL}/str_orange.png`} alt="strength indicator"
                  style={{
                    height: '50px',
                    width: '50px',
                    marginRight: '10px',
                    borderRadius: '5px'
                  }}
                />

          {/* Title */}
          <Typography
            variant="h4"
            component="h1"
            sx={{
              fontFamily: 'Montserrat',
              fontWeight: 'bold',
              flexShrink: 0
            }}
          >
            Skills Tree
          </Typography>

          {/* Spacer */}
          <Box sx={{ flexGrow: 1 }} />

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {/* Help Button */}
            <IconButton
              onClick={handleShowHelp}
              sx={{
                color: 'white',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)'
                }
              }}
              title="Show help"
            >
              <HelpOutlineIcon />
            </IconButton>

            {/* Admin Controls - Hidden in ReadOnly Mode */}
            {!readOnly && (
              <>
                {/* Delete Button */}
                <Button
                  variant="contained"
                  color="error"
                  onClick={handleDeleteClick}
                  size="small"
                >
                  Delete Map
                </Button>

                {/* Back Button */}
                <Button
                  variant="contained"
                  onClick={handleBackToSelection}
                  startIcon={<ArrowBackIcon />}
                  sx={{
                    backgroundColor: '#476999',
                    '&:hover': {
                      backgroundColor: '#3a5577'
                    }
                                   }}
                  size="small"
                >
                  Back
                </Button>
              </>
            )}
          </Box>
        </Box>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteConfirmOpen}
          onClose={() => setDeleteConfirmOpen(false)}
        >
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete the skills tree for this course? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
            <Button onClick={handleConfirmDelete} color="error" variant="contained">
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </HeaderContainer>

      {/* Main Content */}
      <ContentContainer>
        {currentPlan === 2 && !propCourseId ? (
          // Selection View
          <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'flex-start' }}>
            <ControlPanel>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Select a subject and a course
                </Typography>

                <FormControl fullWidth margin="normal">
                  <TextField
                    select
                    label="Subject"
                    value={selectedSubjectForCourses ?? ''}
                    onChange={handleSubjectChange as any}
                   
                    fullWidth
                    margin="normal"
                  >
                    {subjects.map((subject) => (
                      <MenuItem
                        key={subject.SubjectID}
                        value={subject.SubjectID}
                      >
                        {subject.Subject_name}
                      </MenuItem>
                    ))}
                  </TextField>
                </FormControl>

                <FormControl fullWidth margin="normal">
                  <TextField
                    select
                    label="Course"
                    value={selectedCourseForTopics ?? ''}
                    onChange={handleCourseChange as any}
                    fullWidth
                    margin="normal"
                  >
                    {(selectedSubjectForCourses !== null ?
                      coursesBySubject[selectedSubjectForCourses] || [] :
                      []
                    ).length === 0 ? (
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                    ) : (
                      (selectedSubjectForCourses !== null
                        ? coursesBySubject[selectedSubjectForCourses] || []
                        : []
                      ).map((course) => (
                        <MenuItem
                          key={course.CourseID}
                          value={course.CourseID}
                        >
                          {course.Course_name}
                        </MenuItem>
                      ))
                    )}
                  </TextField>
                </FormControl>

                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleGenerateSkillsMap}
                  disabled={selectedCourse === 0 || loading}
                  sx={{ mt: 2, backgroundColor: '#476999' }}
                >
                  {loading ? <CircularProgress size={24} /> : 'View or generate skills map'}
                </Button>
              </CardContent>
            </ControlPanel>
          </Box>
        ) : (
          // Skills Map View
          <>
{/* Canvas Area */}
<Box sx={{ 
  flexGrow: 1, 
  display: 'flex', 
  flexDirection: 'column',
  minWidth: 0, // ✅ ADD: Allow flex item to shrink
  minHeight: 0  // ✅ ADD: Allow flex item to shrink
}}>
  <Typography variant="h6" gutterBottom sx={{ color: 'white' }}>
    {selectedCourse > 0
      ? `Skills Map for ${displayCourseName}`
      : 'Skills Map'}
  </Typography>
  <Box
    sx={{
      flexGrow: 1,
      position: 'relative',
      border: '1px solid #151616',
      borderRadius: 1,
      backgroundColor: '#FDFFEF',
      minHeight: '400px', // ✅ CHANGED: Reduced from 536px
      display: 'flex', // ✅ ADD: Make it a flex container
      alignItems: 'stretch', // ✅ ADD: Stretch children
    }}
  >
    {/* Canvas */}
    <SkillsCanvas
      ref={canvasRef}
      width={canvasDimensions.width}
      height={canvasDimensions.height}
      onMouseMove={handleCanvasMouseMove}
      onMouseLeave={handleCanvasMouseLeave}
      onClick={handleCanvasClick}
      style={{
        display: 'block',
        width: '100%', // ✅ KEEP: Fill parent width
        height: '100%' // ✅ KEEP: Fill parent height
      }}
    />
    
    {/* Tooltip */}
    {hoveredSkill && (
      <Box
        sx={{
          position: 'absolute',
          left: `${hoveredSkill.x}px`,
          top: `${hoveredSkill.y - 40}px`,
          backgroundColor: '#333',
          color: '#fff',
          padding: '8px 12px',
          borderRadius: '4px',
          fontSize: '14px',
          whiteSpace: 'nowrap',
          pointerEvents: 'none',
          zIndex: 10,
          boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
          transform: 'translateX(-50%)',
        }}
      >
        {hoveredSkill.name}
      </Box>
    )}
    {loading && (
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 5,
        }}
      >
        <CircularProgress sx={{ color: 'white' }} />
      </Box>
    )}
  </Box>
</Box>

{/* // In the render section where you use SkillsScoreTable: */}

<Box sx={{ width: '327px', ml: 1, display: 'flex', flexDirection: 'column' }}>
  <Typography variant="h6" gutterBottom sx={{ color: 'white', mb: 1 }}>
    Skill Key
  </Typography>
  
  {/* ✅ Show different table based on whether we have student data */}
  {propIndividualID && propIndividualID > 0 ? (
    // Student view: Show scores and attempts
    <SkillsScoreTable 
      skillNodes={skillNodes}
      showProgress={true}
      competencyScores={(() => {
        // ✅ Convert competencyColors array to Map<SkillID, {score, attempts}>
        const map = new Map<number, { score: number; attempts: number }>();
        competencyColors.forEach(comp => {
          map.set(comp.SkillID, {
            score: comp.aveScore,
            attempts: comp.Attempts || 0
          });
        });
        return map;
      })()}
    />
  ) : (
    // Teacher/Admin view: Show coordinates and structure
    <SkillsIndexTable 
      skillNodes={skillNodes}
    />
  )}
</Box>
          </>
        )}
      </ContentContainer>

      {error && (
        <Alert severity="error" onClose={() => setError('')} sx={{ m: 2 }}>
          {error}
        </Alert>
      )}

      <HelpDialog open={helpOpen} onClose={handleCloseHelp} maxWidth="md" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#A55A02', color: 'white' }}>
          Skills Map Help
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: '#A55A02', color: 'white' }}>
          <Typography variant="body1" paragraph>
            The Skills Map visualizes the relationships between different skills in a course.
          </Typography>
          <Typography variant="body1" paragraph>
            • Each numbered circle represents a skill
          </Typography>
          <Typography variant="body1" paragraph>
            • Lines show prerequisite relationships between skills
          </Typography>
          <Typography variant="body1" paragraph>
            • Skills are arranged in tiers from foundational to advanced
          </Typography>
          <Typography variant="body1" paragraph>
            • Use the skill key table to see detailed information about each skill
          </Typography>
          <Typography variant="body1" paragraph>
            Skills are color-coded based on student performance:
          </Typography>
          <Typography variant="body1" component="div">
            <ul>
              <li>Red (1): Needs significant support</li>
              <li>Orange (2): Developing understanding</li>
              <li>Yellow-green (3): Meeting expectations</li>
              <li>Light green (4): Exceeding expectations</li>
              <li>Green (5): Mastery achieved</li>
            </ul>
          </Typography>
        </DialogContent>
        <DialogActions sx={{ backgroundColor: '#A55A02' }}>
          <Button onClick={handleCloseHelp} sx={{ color: 'white' }}>
            Close
          </Button>
        </DialogActions>
      </HelpDialog>


<SkillDetailsDialog
  open={skillDetailsOpen}
  onClose={() => {
    setSkillDetailsOpen(false);
    setSkillResources([]);
  }}
  skillDetails={selectedSkillDetails}
  skillNodes={skillNodes}
  readOnly={!effectiveIndividualID || effectiveIndividualID <= 0} // ✅ FIX: Only read-only if no valid ID
  workAssignments={workAssignments}
  resources={skillResources}
  onTabChange={handleTabChange}
/>
    </Box>
  );
};

export default SkillsTree;


